import React, { useContext, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { GlassBox } from "src/propertyInfoTemplates/PropertyComponents";
import { GlassButton } from "src/components/GlassButtons";
import { GlassInputGroup, GlassInputControlLabel, GlassInputControl } from "src/components/GlassInput";
import { MapitStateContext } from "src/states/MapitState";
import { LayerType, type LayerInfo, LayerVariabilityType } from "src/common/managers/Types";
import { ANNManager } from "./ANNManager";
import { AnnotationType, AnnotationContextType, type ANNAnnotationRecord, type ANNLayerSnapshot } from "./ANNTypes";
import { snapshotLayer } from "./ANNFunc";
import { ANNActionType, ANNContext } from "./ANNState";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { BsCheckCircle } from "react-icons/bs";
interface BaseCreateModalProps {
   open: boolean;
   contextType: AnnotationContextType;
   documentId?: string;
   bfeNumber?: string | number;
   datoloebenr?: string;
   onClose: () => void;
   onCreated?: (annotation: ANNAnnotationRecord) => void;
}
export interface ANNCreateStedfaestelseProps extends BaseCreateModalProps { }
export function ANNCreateStedfaestelse(props: ANNCreateStedfaestelseProps) {
   const { open, contextType, documentId, bfeNumber, datoloebenr, onClose, onCreated } = props;
   const { state: sessionState } = useContext(SessionContext);
   const { state: mapitState } = useContext(MapitStateContext);
   const { dispatch: annDispatch } = useContext(ANNContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);
   const organizationId = sessionState?.customerRef;
   const [title, setTitle] = useState("");
   const [historyComment, setHistoryComment] = useState("");
   const [verifyStamp, setVerifyStamp] = useState(false);
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [selectedLayerIds, setSelectedLayerIds] = useState<number[]>([]);
   const availableLayers = useMemo(() => {
      return Object.values(mapitState.layers || {})
         .filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer)
         .map(layer => {
            const snapshot = snapshotLayer(layer);
            return snapshot ? { layer, snapshot } : null;
         })
         .filter((item): item is { layer: LayerInfo; snapshot: ANNLayerSnapshot } => item !== null);
   }, [mapitState.layers]);
   const layersToPersist = useMemo(() => {
      return availableLayers
         .filter(item => selectedLayerIds.includes(item.layer.layerId))
         .map(item => item.snapshot);
   }, [availableLayers, selectedLayerIds]);
   if (!open) return null;
   function handleAddLayer(layerId: number) {
      if (!selectedLayerIds.includes(layerId)) {
         setSelectedLayerIds([...selectedLayerIds, layerId]);
      }
   }
   function handleRemoveLayer(layerId: number) {
      setSelectedLayerIds(selectedLayerIds.filter(id => id !== layerId));
   }
   async function handleCreate() {
      if (contextType === AnnotationContextType.Servitut && !documentId) {
         setError(Localization.getText("No document selected"));
         return;
      }
      if (contextType === AnnotationContextType.Ejendom && !bfeNumber) {
         setError(Localization.getText("No BFE number available"));
         return;
      }
      if (!organizationId) {
         setError(Localization.getText("No organization selected"));
         return;
      }
      if (!title.trim()) {
         setError(Localization.getText("Title is required"));
         return;
      }
      if (!layersToPersist.length) {
         setError(Localization.getText("Please select at least one layer"));
         return;
      }

      const liveLayerSnapshots = selectedLayerIds
         .map((id) => {
            const layer = mapitState.layers?.[id];
            return layer ? snapshotLayer(layer) : undefined;
         })
         .filter((s): s is ANNLayerSnapshot => !!s);

      if (!liveLayerSnapshots.length) {
         setError(Localization.getText("Please select at least one layer"));
         return;
      }
      setSaving(true);
      setError(null);
      annDispatch({ type: ANNActionType.SaveAnnotationRequest });
      try {
         const record = await ANNManager.saveAnnotation({
            contextType,
            documentId,
            bfeNumber,
            datoloebenr,
            organizationId,
            type: AnnotationType.Stedfaestelse,
            title: title.trim(),
            historyComment: historyComment.trim() || undefined,
            verified: canVerify && verifyStamp,
            userId: sessionState?.userRef || "anonymous",
            userName: sessionState?.userName || sessionState?.userRef || "anonymous",
            layers: liveLayerSnapshots,
         });
         annDispatch({ type: ANNActionType.SaveAnnotationSuccess, payload: record });
         onCreated?.(record);
         setTitle("");
         setHistoryComment("");
         onClose();
      } catch (err: any) {
         annDispatch({ type: ANNActionType.SaveAnnotationFailure, payload: err?.message || String(err) });
         setError(err?.message || String(err));
      } finally {
         setSaving(false);
      }
   }
   return ReactDOM.createPortal(
      <AdvancedDragModal
         title={Localization.getText("Create stedfaestelse")}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ left: "25%", top: "15%", width: "520px" }}
         onClose={onClose}
      >
         <ADModalBody>
            <GlassBox title={<div className="ann-create-stedfaestelse-title">
               {Localization.getText("Create stedfaestelse")}
            </div>}>
               <GlassInputGroup>
                  <GlassInputControlLabel>{Localization.getText("Title")}</GlassInputControlLabel>
                  <GlassInputControl>
                     <input value={title} onChange={e => setTitle(e.target.value)} />
                  </GlassInputControl>
                  <GlassInputControlLabel>{Localization.getText("History comment")}</GlassInputControlLabel>
                  <GlassInputControl>
                     <textarea rows={3} value={historyComment} onChange={e => setHistoryComment(e.target.value)} placeholder={Localization.getText("Optional history comment")} />
                  </GlassInputControl>
                  {canVerify && (
                     <>
                        <GlassInputControlLabel>{Localization.getText("Verification")}</GlassInputControlLabel>
                        <GlassInputControl>
                           <label
                              className={`ann-create-verifyCheckboxLabel ${verifyStamp ? "ann-create-verifyCheckboxLabel--checked" : ""}`}
                           >
                              <input
                                 type="checkbox"
                                 checked={verifyStamp}
                                 onChange={(e) => setVerifyStamp(e.target.checked)}
                                 className="ann-create-verifyCheckbox"
                              />
                              <BsCheckCircle
                                 size={20}
                                 className="ann-create-verifyIcon"
                              />
                              <span className="ann-create-verifyText">
                                 {Localization.getText("Stamp this annotation as verified")}
                              </span>
                           </label>
                        </GlassInputControl>
                     </>
                  )}
                  <GlassInputControlLabel>{Localization.getText("Select layers to save")}</GlassInputControlLabel>
                  <GlassInputControl>
                     {availableLayers.length === 0 ? (
                        <div className="ann-create-stedfaestelse-error">
                           {Localization.getText("No layers available")}
                        </div>
                     ) : (
                        <>
                           <select
                              value=""
                              onChange={(e) => {
                                 const layerId = parseInt(e.target.value);
                                 if (layerId && !selectedLayerIds.includes(layerId)) {
                                    handleAddLayer(layerId);
                                 }
                                 e.target.value = "";
                              }}
                              className="ann-create-stedfaestelse-select"
                           >
                              <option value="">{Localization.getText("Add a layer...")}</option>
                              {availableLayers
                                 .filter(item => !selectedLayerIds.includes(item.layer.layerId))
                                 .map(item => (
                                    <option key={item.layer.layerId} value={item.layer.layerId}>
                                       {item.layer.datasetname || item.layer.filename || `Layer ${item.layer.layerId}`}
                                       {item.layer.type === LayerType.ImageOverlay ? " (Image)" :
                                          item.layer.type === LayerType.GeoJSON_Line ? " (Line)" :
                                             item.layer.type === LayerType.GeoJSON_Polygon ? " (Polygon)" : ""}
                                    </option>
                                 ))}
                           </select>
                           {selectedLayerIds.length > 0 && (
                              <div className="ann-create-stedfaestelse-selected-layers">
                                 <div className="ann-create-stedfaestelse-selected-layers-header">
                                    {Localization.getText("Selected layers")} ({selectedLayerIds.length}):
                                 </div>
                                 {layersToPersist.map((snapshot, idx) => {
                                    const layerInfo = availableLayers.find(item => item.layer.layerId === snapshot.layerId)?.layer;
                                    return (
                                       <div
                                          key={snapshot.layerId}
                                          className="ann-create-stedfaestelse-layer-item"
                                       >
                                          <span className="ann-create-stedfaestelse-layer-name">
                                             {layerInfo?.datasetname || layerInfo?.filename || `Layer ${snapshot.layerId}`}
                                          </span>
                                          <button
                                             type="button"
                                             onClick={() => handleRemoveLayer(snapshot.layerId!)}
                                             className="ann-create-stedfaestelse-layer-remove"
                                             title={Localization.getText("Remove")}
                                          >
                                             ×
                                          </button>
                                       </div>
                                    );
                                 })}
                              </div>
                           )}
                        </>
                     )}
                  </GlassInputControl>
                  {error ? (
                     <GlassInputControl>
                        <div className="ann-create-stedfaestelse-error">{error}</div>
                     </GlassInputControl>
                  ) : null}
               </GlassInputGroup>
               <div className="ann-create-stedfaestelse-button-group">
                  <GlassButton disabled={saving} onClick={handleCreate}>
                     {saving ? Localization.getText("Saving...") : Localization.getText("Create")}
                  </GlassButton>
                  <GlassButton onClick={onClose}>{Localization.getText("Cancel")}</GlassButton>
               </div>
            </GlassBox>
         </ADModalBody>
      </AdvancedDragModal>,
      document.body
   );
}
